import { Component, Inject, Input } from '@angular/core';
import { validateBasis } from '@angular/flex-layout';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ContractDetail } from 'src/app/models/ContractDetail';
import { ContractDetailsCreate } from 'src/app/models/ContractDetailsCreate';
import { UploadContractDetailsResponse } from 'src/app/models/UploadContractDetailsResponse';
import { ContractDetailService } from 'src/app/services/contract-detail.service';

import { CreateEditDetailData } from './create-edit-detail-data';
import { UploadContractDetailData } from './upload-contract-detail-data';

@Component({
  selector: 'app-create-edit-detail',
  templateUrl: './create-edit-detail.component.html',
  styleUrls: ['./create-edit-detail.component.less']
})
export class CreateEditDetailComponent {
  editMode: boolean = true;
  saveDisabled: boolean = true;
  isLoadingResults: boolean = false;
  detailsForm: FormGroup;
  detail: ContractDetail;

  fileUploadData: any;
  validationErrorMessage = '';
  warningMessage = 'Bulk upload. All current contract details will be deleted.'
  detailEntries: ContractDetail[] = [];
  binLength = 0;
  binNumeric = 0;
  groupLength = 0;
  pcnLength = 0;
  networkIdLength = 0;
  duplicateRecords = 0;
  emptyRows = 0;

  constructor(
    public dialogRef: MatDialogRef<CreateEditDetailComponent>,
    private fb: FormBuilder,
    private contractDetailsService: ContractDetailService,
    @Inject(MAT_DIALOG_DATA) public data: CreateEditDetailData
  ) {
    if(this.data.action === 'add') {
      this.editMode = false;
    }
  }

  ngOnInit(): void {
    this.detailsForm = this.fb.group({
      binCtrl: ['', Validators.pattern("[0-9]{6,}$")],
      groupNumberCtrl: [''],
      pcnCtrl: [''],
      networkIdCtrl: ['']
    });
    this.detailsForm.addValidators(this.atLeastOneValidator());
    this.populateFieldsIfEdit();
    this.loadClaimDetailFileData();
  }

  loadClaimDetailFileData() {
    this.fileUploadData = {
      fileUploadComponent: 'DetailsUpload',
      contractId: this.data.detail.contractId,
      maxFileSize: 10240000,
      acceptedFileTypes: ['text/csv'],
      message: 'Drag and drop .csv here',
      topMessage: '1 file only. 10MB limit.',
      errorMessage: 'Single CSV file only. Max limit 10MB. No duplicate entries.',
      checkForDuplicateNames: true,
      maxNumberOfFiles: 1
    };
  }

  uploadContractDetailCsv(upload: UploadContractDetailsResponse) {
    if (upload.status) {
      this.validateAndAddContractDetailRecords(upload.contractDetails, this.data.detail.contractId);

      if (!this.hasInvalidRecords && this.detailEntries.length > 0) {
        this.uploadContractDetails(this.data.detail.contractId)
      }
    }
    else {
      this.validationErrorMessage = '';
    }
  }

  hasInvalidRecords = false;
  contractDetailsArray: string[] = [];
  validateAndAddContractDetailRecords(records: string[], contractId: number) {
    // parse through records and add to array to determine if there are duplicates
    this.contractDetailsArray = [];
    this.binLength = 0;
    this.binNumeric = 0;
    this.pcnLength = 0;
    this.networkIdLength = 0;
    this.groupLength = 0;
    this.hasInvalidRecords = false;
    this.duplicateRecords = 0;
    // start by going through the body of records - contract detail items in the csv
    if (records.length > 0) {
      for (let item = 0; item < records.length; item++) {

        // split into an array based on the comma
        let data = records[item].split(',');

        if (data.length != 4) {
          // throw an error - invalid
        }

        var bin = data[0].trim();
        var pcn = data[1].trim();
        var group = data[2].trim();
        var networkId = data[3].trim();

        // network id can be blank or null but cannot be longer than 20 characters
        if (networkId.length > 20) {
          this.networkIdLength++;
        }

        // pcn cannot have a value greater than 20
        if (pcn.length > 20) {
          this.pcnLength++;
        }

        // group cannot have a value greater than 15
        if (group.length > 15) {
          this.groupLength++;
        }

        // bin can be blank, but must be 6 characters in length and must be numeric
        if (bin.length != 6 && bin.length > 1) {
          this.binLength++;
        }

        if (!this.isNumber(bin) && bin.length > 1) {
          this.binNumeric++;
        }

        // ok ... create a string based on all 4 values (null for blanks)
        let allDetails = (networkId == null || networkId.length < 1 ? 'null' : networkId) + (pcn == null || pcn.length < 1 ? 'null' : pcn) + (group == null || group.length < 1 ? 'null' : group) + (bin == null || bin.length < 1 ? 'null' : bin);

        if (this.contractDetailsArray.includes(allDetails)) {
          // we have a duplicate
          this.duplicateRecords++;
        }
        else {
          this.contractDetailsArray.push(allDetails);
        }

        // rows cannot be empty
        if (bin.length < 1 && pcn.length < 1 && group.length < 1 && networkId.length < 1) {
          this.emptyRows++;
        }

        // there could be thousands of records here, so circumvent the process if we have an error
        if (this.binLength > 0 || this.binNumeric > 0 || this.pcnLength > 0 || this.groupLength > 0 || this.networkIdLength > 0 || this.duplicateRecords > 0 || this.emptyRows > 0) {
          this.hasInvalidRecords = true;
          this.detailEntries = [];
        }

        // now add a detail entry as long as there are no invalid records
        if (!this.hasInvalidRecords) {
          this.detailEntries.push({'contractId': contractId, 'contractDetailId': 0, 'bin': bin, 'networkId': networkId, 'groupNumber': group, 'pcn': pcn});
        }
      }
    }

    // create proper error message to display
    if (this.binNumeric > 0) {
      this.validationErrorMessage += +this.binNumeric + ' invalid BIN' + (this.binNumeric > 1 ? 's' : '') + ' found in the file. Only numbers are allowed. <br/>';
      this.binNumeric = 0;
      this.hasInvalidRecords = true;
    }
    if (this.binLength > 0) {
      this.validationErrorMessage += +this.binLength + ' BIN' + (this.binLength > 1 ? 's' : '') + ' exceed' + (this.binLength < 2 ? 's' : '') + ' 6 digits. <br/>';
      this.binLength = 0;
      this.hasInvalidRecords = true;
    }
    if (this.pcnLength > 0) {
      this.validationErrorMessage += +this.pcnLength + ' PCN' + (this.pcnLength > 1 ? 's' : '') + ' exceed' + (this.pcnLength < 2 ? 's' : '') + ' 20 characters. <br/>';
      this.pcnLength = 0;
      this.hasInvalidRecords = true;
    }
    if (this.groupLength > 0) {
      this.validationErrorMessage += +this.groupLength + ' Group' + (this.groupLength > 1 ? 's' : '') + ' exceed' + (this.groupLength < 2 ? 's' : '') + ' 15 characters. <br/>';
      this.groupLength = 0;
      this.hasInvalidRecords = true;
    }
    if (this.networkIdLength > 0) {
      this.validationErrorMessage += +this.networkIdLength + ' Network' + (this.networkIdLength > 1 ? 's' : '') + ' exceed' + (this.networkIdLength < 2 ? 's' : '') + ' 20 characters. <br/>'
      this.networkIdLength = 0;
      this.hasInvalidRecords = true;
    }
    if (this.emptyRows > 0) {
      this.validationErrorMessage += +this.emptyRows + ' empty row' + (this.emptyRows > 1 ? 's' : '') + '. <br/>';
      this.emptyRows = 0;
      this.hasInvalidRecords = true;
    }
    if (this.duplicateRecords > 0) {
      this.validationErrorMessage += this.duplicateRecords + ' duplicate Contract Detail' + (this.duplicateRecords > 1 ? 's' : '') + ' found in the file. <br/>';
      this.duplicateRecords = 0;
      this.hasInvalidRecords = true;
    }

    if (this.hasInvalidRecords) {
      this.validationErrorMessage = '<b class="error-messages">* FILE UPLOAD FAILED</b><br/>' + this.validationErrorMessage;
    }

    if (records.length < 1) {
      this.binLength = 0;
      this.pcnLength = 0;
      this.groupLength = 0;
      this.networkIdLength = 0;
      this.emptyRows = 0;
      this.duplicateRecords = 0;
      this.hasInvalidRecords = true;
      this.validationErrorMessage = '<b class="error-messages">* NO DETAILS UPLOADED.  CSV FILE IS EMPTY</b><br/>' + this.validationErrorMessage;
    }

  }

  uploadContractDetails(contractId: number) {
    // get data from csv and add to array of contract details
    this.isLoadingResults = true;
    var contractDetailsCreate: ContractDetailsCreate = {
      contractDetails: this.detailEntries
    }

    this.contractDetailsService.createContractDetailsBulk(contractId, contractDetailsCreate)
      .subscribe({
        next: (result) => {
          const returnData: UploadContractDetailData = {
            action: 'close',
            title: '',
            contractId: this.data.detail.contractId
          }
          toastr.success('Contract details were uploaded successfully!')
          this.dialogRef.close(returnData);
          this.isLoadingResults = false;
        },
        error: (err) => {
          toastr.error('We are unable to upload Contract Details at this time.')
          this.isLoadingResults = false;
          console.log(err);
        }
      });

  }


  isNumber(value?: string | number): boolean {
    return ((value != null) &&
      (value !== '') &&
      !isNaN(Number(value.toString())));
  }


  populateFieldsIfEdit() {
    this.detail = this.data.detail;
    if (this.editMode) {
      this.detailsForm.controls.binCtrl.setValue(this.detail.bin);
      this.detailsForm.controls.groupNumberCtrl.setValue(this.detail.groupNumber);
      this.detailsForm.controls.pcnCtrl.setValue(this.detail.pcn);
      this.detailsForm.controls.networkIdCtrl.setValue(this.detail.networkId);
    }
  }

  checkSaveEnabled(): void {
    this.saveDisabled =
      this.detailsForm.pristine ||
      this.detailsForm.invalid ||
      this.checkValuesChange();
  }

  checkValuesChange(): boolean {
    if (this.editMode) {
      const bin = this.detailsForm.controls.binCtrl.value;
      const groupNumber = this.detailsForm.controls.groupNumberCtrl.value;
      const pcn = this.detailsForm.controls.pcnCtrl.value;
      const networkId = this.detailsForm.controls.networkIdCtrl.value;
      return (
        bin === this.detail.bin &&
        groupNumber === this.detail.groupNumber &&
        pcn === this.detail.pcn &&
        networkId === this.detail.networkId
      );
    }
    return false;
  }

  saveContractDetails() {
    const contractDetailRequestModel: ContractDetail = {
      contractDetailId: this.detail.contractDetailId,
      bin: this.getStringValueOrNull(this.detailsForm.controls.binCtrl.value),
      groupNumber: this.getStringValueOrNull(this.detailsForm.controls.groupNumberCtrl.value),
      pcn: this.getStringValueOrNull(this.detailsForm.controls.pcnCtrl.value),
      networkId: this.getStringValueOrNull(this.detailsForm.controls.networkIdCtrl?.value),
      contractId: this.detail.contractId
    }

    if (this.editMode) {
      this.isLoadingResults = true;
      this.contractDetailsService.updateContractDetails(this.detail.contractId, contractDetailRequestModel)
        .subscribe({
          next: (result) => {
            const returnData: CreateEditDetailData = {
              action: 'close',
              title: '',
              detail: result
            }
            toastr.success('Contract details was updated successfully!')
            this.dialogRef.close(returnData);
            this.isLoadingResults = false;
          },
          error: (err) => {
            toastr.error('We are unable to create contract Details at this time.')
            this.isLoadingResults = false;
            console.log(err)
          }
        });
    }
    else {
      this.isLoadingResults = true;
      var details: ContractDetail[] = [];
      details.push(contractDetailRequestModel)
      var contractDetailsCreate: ContractDetailsCreate = {
        contractDetails: details
      }

      this.contractDetailsService.createContractDetails(this.detail.contractId, contractDetailsCreate)
        .subscribe({
          next: (result) => {
            const returnData: CreateEditDetailData = {
              action: 'close',
              title: '',
              detail: result[0] // Set to 0 because there's only one value returned
            }
            toastr.success('Contract detail was created successfully!')
            this.dialogRef.close(returnData);
            this.isLoadingResults = false;
          },
          error: (err) => {
            toastr.error('We are unable to create contract details at this time.')
            this.isLoadingResults = false;
            console.log(err);
          }
        });
    }
  }

  private atLeastOneValidator = () => {
    return (controlGroup) => {
        let controls = controlGroup.controls;
        if ( controls ) {
            var theOne = Object.keys(controls).find(key=> this.getStringValueOrNull(controls[key].value) !== null);
            if ( !theOne) {
                return {
                    atLeastOneRequired : {
                        text : 'At least one should have a value.'
                    }
                }
            }
        }
        return null;
    };
  };

  getStringValueOrNull(value: string | null | undefined): string | null {
    return value === "" ? null : value ?? null;
  }
}
